import { lodash } from '@avantia/lodash';
import { createControlId, HpAnswerValueTypes, HpButtonStyles } from '@avantia/questionset-model';
import { SvgIcon } from '@avantia/style-guide';
import { BaseSyntheticEvent } from 'react';
import { HpClientDebugFlags } from '../../../clientDebugFlags';
import { logWarning } from '../../../clientLogging';
import { HpClientQuestionLookupItem, HpClientQuestionLookupItemMap } from '../../../types';
import { HpEventDetail, HpOnClickEvent } from '../../../types/inputControlTypes';
import { createSpaceKeyAsClickHandler } from '../controlEvents';

export type HpBasicSwitchTypes = 'switch' | 'button' | 'save-button';

export interface HpBasicSwitchControlProps {
  createSwitchCustomButton?: (props: HpCreateSwitchButtonProps) => JSX.Element;
  data: HpClientQuestionLookupItemMap;
  disabled?: boolean;
  eventDetail: HpEventDetail;
  id: string;
  onClick: HpOnClickEvent;
  type: HpBasicSwitchTypes;
  value: string;
}

export function BasicSwitchControl({
  createSwitchCustomButton,
  data,
  disabled = false,
  eventDetail,
  id,
  onClick,
  type,
  value
}: HpBasicSwitchControlProps): JSX.Element {
  const options: JSX.Element[] = [];
  const allKeys = lodash.keys(data);
  const validKeys = allKeys.filter((k) => k === 'true' || k === 'false');
  if (validKeys.length !== 2 || validKeys.length !== allKeys.length) {
    logWarning(
      `The only valid lookup keys for switches are 'true' and 'false'. Instead ${id} has ["${allKeys.join('", "')}"]`,
      HpClientDebugFlags.Controls
    );
  }

  validKeys.forEach((key) => {
    const option: HpClientQuestionLookupItem | null = data[key];
    const { icon, iconWidth } = option || {};
    let { text } = option || {};
    if (!icon && !text) {
      text = key;
    }

    const isChecked = value === key;
    const clickHandler = (e: BaseSyntheticEvent): void => onClick(e as any, eventDetail);
    const itemId = createControlId({ questionId: id, key });
    const onKeyPressFunc = createSpaceKeyAsClickHandler((ev) => {
      const event = { ...ev, currentTarget: document.getElementById(itemId) };
      clickHandler(event);
    });

    let item: JSX.Element | null = null;
    const labelId = createControlId({ questionId: id, key, options: { customPrefix: 'L' } });
    const isButtonType = type.endsWith('button');
    const className = getClassNameForKey({ isButtonType, type, key, value, text, icon, disabled });

    if (isButtonType) {
      if (value !== key) {
        const buttonProps: HpCreateSwitchButtonProps = {
          key,
          id,
          itemId,
          isChecked,
          onClick: clickHandler,
          disabled: disabled || false,
          className,
          icon: icon ? (
            <SvgIcon type={icon as any} size={(typeof iconWidth === 'string' ? iconWidth : null) || 'small'} />
          ) : null,
          text: text || ''
        };
        item = (createSwitchCustomButton || createSwitchButtonDefault)(buttonProps);
      }
    } else {
      item = (
        <label key={key} id={labelId} className={className} tabIndex={0} onKeyPress={onKeyPressFunc}>
          <input
            autoComplete="off"
            checked={isChecked}
            disabled={disabled || false}
            id={itemId}
            name={id}
            onChange={clickHandler}
            onClick={clickHandler}
            tabIndex={-1}
            type="radio"
            value={key}
          />
          <span className="av-label-text">{text}</span>
        </label>
      );
    }

    if (item) {
      options.push(item);
    }
  });

  return (
    <div id={createControlId({ questionId: id })} role="group" className="btn-group">
      {options}
    </div>
  );
}

export interface HpCreateSwitchButtonProps {
  className: string | undefined;
  disabled: boolean;
  icon: JSX.Element | null;
  id: string;
  isChecked: boolean;
  itemId: string;
  key: string;
  onClick: (e: BaseSyntheticEvent) => void;
  text: string;
}

export function createSwitchButtonDefault({
  className,
  disabled,
  icon,
  id,
  itemId,
  key,
  onClick,
  text
}: HpCreateSwitchButtonProps): JSX.Element {
  return (
    <button key={key} id={itemId} name={id} onClick={onClick} disabled={disabled} type="button" className={className}>
      {icon}
      {text}
    </button>
  );
}

interface HpGetClassNameForKeyProps {
  disabled: boolean;
  icon?: string;
  isButtonType: boolean; // if true, it is not a Switch; this is derived from 'type'.
  key: string;
  text?: string;
  type: HpBasicSwitchTypes;
  value: HpAnswerValueTypes;
}

function getClassNameForKey({
  isButtonType,
  type,
  key,
  value,
  text,
  icon,
  disabled
}: HpGetClassNameForKeyProps): string | undefined {
  const classNames: string[] = [];
  if (isButtonType) {
    // type: either 'save-button' or 'button'
    if (value !== key) {
      const isSaveButton = type === 'save-button';
      let style: HpButtonStyles;
      if (key === 'false') {
        if (isSaveButton) {
          style = 'outline-primary';
          classNames.push('hp-button-save');
        } else {
          style = 'danger';
        }
      } else {
        if (isSaveButton) {
          style = 'outline-primary';
          classNames.push('hp-button-edit');
        } else {
          style = 'primary';
        }
      }

      classNames.push('btn', `btn-${style}`);
    }
  } else {
    // type: 'switch'
    classNames.push('btn', `btn-${value === key ? 'primary' : 'outline-primary'}`);
  }

  if (!text && icon) {
    classNames.push('hp-icon-only');
  }

  if (disabled) {
    classNames.push('disabled');
  }

  return classNames.length === 0 ? undefined : classNames.sort().join(' ');
}
